import React, {useEffect} from 'react';

import TabbedModal, {
  StyledModalFooter,
} from 'app/components/general/TabbedModal';
import StateButton from 'app/general/widgets/StateButton';
import {useBulkCancelTasks, useBulkCancelTasksSeries} from 'app/tasks/api';
import {repeats} from 'app/tasks/utils';
import {gt, ngt} from 'app/utils/i18n';

import {
  SlimTaskInstanceSerializedType,
  TaskInstanceSerializedType,
} from 'sweet-types';

interface Props {
  show: boolean;
  tasks: Array<TaskInstanceSerializedType | SlimTaskInstanceSerializedType>;
  close: () => void;
  onClosed: () => void;
}

export function CancelTaskModal({
  show,
  tasks,
  close,
  onClosed,
}: Props): JSX.Element | null {
  const [cancelSingle, setCancelSingle] = React.useState(true);

  const [bulkCancelTasks, {isLoading: isCancelling, isSuccess: singleSuccess}] =
    useBulkCancelTasks();
  const [
    bulkCancelTasksSeries,
    {isLoading: isDeleting, isSuccess: seriesSuccess},
  ] = useBulkCancelTasksSeries();

  const isBusy = isCancelling || isDeleting;
  const isSuccess = singleSuccess || seriesSuccess;

  const isSingle = tasks.every((task) => !repeats(task.template.rrule));

  const taskinstances = tasks.map((tasks) => tasks.url);

  useEffect(() => {
    if (isSuccess) {
      close();
    }
  }, [isSuccess]);

  return (
    <TabbedModal
      show={show}
      onCancel={close}
      onClosed={onClosed}
      tabs={[
        {
          eventKey: 'main-tab',
          name: ngt('Cancel Task', 'Cancel Tasks', tasks.length),
          icon: 'fa fa-tasks',
          content: (
            <div>
              {isSingle ? (
                <p>
                  {ngt(
                    'Are you sure you want to cancel this task?',
                    'Are you sure you want to cancel the selected tasks?',
                    tasks.length
                  )}
                </p>
              ) : (
                <div>
                  {' '}
                  <p>
                    {ngt(
                      'Would you like to cancel just this one task, or also the following tasks in this series?',
                      'Would you like to cancel just the selected tasks, or also the following tasks for each series?',
                      tasks.length
                    )}
                  </p>
                  <div className="mx-5 mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        id="cancel-task-modal-cancel-instance-radio"
                        type="radio"
                        onChange={() => setCancelSingle(true)}
                        checked={cancelSingle}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="cancel-task-modal-cancel-instance-radio"
                      >
                        {ngt(
                          'Cancel only this task',
                          'Cancel only selected tasks',
                          tasks.length
                        )}
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        id="cancel-task-modal-cancel-series-radio"
                        type="radio"
                        onChange={() => setCancelSingle(false)}
                        checked={!cancelSingle}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="cancel-task-modal-cancel-series-radio"
                      >
                        {ngt(
                          'Cancel this task and following ones',
                          'Cancel selected tasks and the following ones for each series',
                          tasks.length
                        )}
                      </label>
                    </div>
                  </div>
                </div>
              )}
              <StyledModalFooter>
                <button className="btn btn-link text-secondary" onClick={close}>
                  <i className="fa fa-times mr-1" />
                  {gt('Close')}
                </button>
                <StateButton
                  className="btn btn-primary"
                  disabled={isBusy}
                  busyText={gt('Canceling')}
                  isIdle={!isBusy}
                  onClick={() =>
                    cancelSingle
                      ? bulkCancelTasks({taskinstances})
                      : bulkCancelTasksSeries({taskinstances})
                  }
                >
                  {gt('Confirm')}
                </StateButton>
              </StyledModalFooter>
            </div>
          ),
        },
      ]}
    />
  );
}
